import { z } from 'zod';

import { apiFetch } from '@/lib/api-client';

/**
 * Ingestion API client (SCRUM-677) — the deposit half of INGESTION-FRAMEWORK.md §2.
 *
 * The upload is a TWO-STEP, direct-to-COS flow so the file bytes never transit the API:
 *   1. POST /api/ingestion/uploads → the server mints a short-lived presigned PUT URL scoped to
 *      the tenant's landing bucket (tenant from the JWT claim for a bank caller; a validated target
 *      tenant for a Platform Admin).
 *   2. PUT the file bytes directly to that URL with `Content-Type: text/csv` (the signature binds
 *      the content type). This request carries NO Bearer — the presigned signature IS the auth.
 *
 * The poller (job-poller profile) then enqueues an IngestionJob for the deposited file on its next
 * cycle, so the file is picked up asynchronously (not instantly) — the UI messages this.
 */
const INGESTION_UPLOADS_URL = '/api/ingestion/uploads';
// ↑ Swap-point: nginx proxies /api/* to bankiq-api in the deployed container.

/**
 * IC-42 report types the uploader chooses from (SCRUM-688). The `code` is validated server-side
 * against the resolver's known set and written as an authoritative segment of the object key, so
 * the consumer never has to guess the report type from the (ambiguous) core-export filename. Labels
 * are grouped by canonical family; codes mirror `Ic42ReportTypeResolver.TokenToFamily`.
 */
export const REPORT_TYPES: readonly { code: string; label: string }[] = [
  { code: 'B39', label: 'Customer Master (B39)' },
  { code: 'B3C', label: 'Account Snapshot — B3C' },
  { code: 'B3E', label: 'Account Snapshot — B3E' },
  { code: 'B3B', label: 'Account Snapshot — B3B' },
  { code: 'B36', label: 'Account Snapshot — B36' },
  { code: 'B35', label: 'Transaction Feed — B35' },
  { code: 'B37', label: 'Transaction Feed — B37' },
  { code: 'B33', label: 'Transaction Feed — B33' },
  { code: 'BANK', label: 'Transaction Feed — BANK' },
];

const uploadTicketSchema = z.object({
  url: z.string(),
  objectKey: z.string(),
  expiresInSeconds: z.number(),
});

export type IngestionUploadTicket = z.infer<typeof uploadTicketSchema>;

const ingestionErrorSchema = z.object({ error: z.string(), message: z.string() });

async function throwIngestionError(res: Response, fallback: string): Promise<never> {
  let message = fallback;
  try {
    const parsed = ingestionErrorSchema.safeParse(await res.json());
    if (parsed.success) message = parsed.data.message;
  } catch {
    // Non-JSON body — keep the fallback.
  }
  throw new Error(message);
}

/**
 * Step 1 — request a presigned PUT URL. `tenantId` is sent ONLY by a Platform Admin uploading on
 * behalf of another tenant; a bank caller omits it (the server uses their JWT-claim tenant and
 * rejects any supplied tenantId).
 */
export async function requestUploadUrl(
  fileName: string,
  reportType: string,
  tenantId?: string,
): Promise<IngestionUploadTicket> {
  const body: { fileName: string; reportType: string; tenantId?: string } = {
    fileName,
    reportType,
  };
  if (tenantId) body.tenantId = tenantId;

  const res = await apiFetch(INGESTION_UPLOADS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    await throwIngestionError(res, `Could not prepare the upload for ${fileName}.`);
  }
  const raw: unknown = await res.json();
  return uploadTicketSchema.parse(raw);
}

/**
 * Step 2 — PUT the file bytes directly to the presigned COS URL. Uses the global `fetch` (NOT
 * apiFetch) so no Authorization header is attached: the presigned signature is the authorization,
 * and COS rejects a request carrying an unexpected header. The `Content-Type` MUST match the value
 * the URL was signed for (`text/csv`).
 */
export async function putFileToPresignedUrl(url: string, file: File): Promise<void> {
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'text/csv' },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Upload to storage failed (${String(res.status)}).`);
  }
}

/**
 * Full deposit: request the URL, then PUT the bytes. Returns the object key the file landed at.
 */
export async function uploadIngestionFile(
  file: File,
  reportType: string,
  tenantId?: string,
): Promise<string> {
  const ticket = await requestUploadUrl(file.name, reportType, tenantId);
  await putFileToPresignedUrl(ticket.url, file);
  return ticket.objectKey;
}
